/* eslint-disable no-console */
import { Trans } from '@lingui/react/macro';
import { DialogTrigger } from '@repo/videa-components/src/ui/dialog';
import classNames from 'classnames';
import { useAtom } from 'jotai';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import { isViewingEngagementAtom } from '@/modules/insights/atoms';

import { isDirectorUser } from '../../modules/login/login-utils';
import { useShouldHideHuddle } from '../../modules/patientList/useShouldHideHuddle';
import { usePreferences } from '../../react-query/usePreferences';
import routesConstants from '../../routes/routes.constants';
import {
  directorPracticeIdAtom,
  useDirectorPracticeView,
} from '../../utils/hooks/useDirectorPracticeView';
import { PracticeFeatureFlagKey, useFeatureFlag } from '../../utils/user';

import { PracticesDialog } from './PracticesDialog';

type NavigationItemProps = {
  children: React.ReactNode;
  dataCy?: string;
  isSelected?: boolean;
  onClick?: () => void;
};

const NavigationItem = ({
  children,
  dataCy,
  isSelected,
  onClick,
}: NavigationItemProps) => {
  return (
    <button
      className={classNames(
        'vai-block vai-text-lg vai-text-white vai-cursor-pointer vai-border-0 vai-bg-transparent vai-p-0 vai-m-0 vai-outline-none focus:vai-ring-0 select-none focus:vai-outline-none active:vai-bg-transparent',
        'hover:vai-opacity-80 hover:vai-border-b-2 hover:vai-border-sky-400/50',
        !isSelected && 'vai-border-b-2 vai-border-transparent',
        isSelected &&
          'vai-font-semibold vai-border-b-2 vai-border-solid vai-border-sky-400',
      )}
      data-cy={dataCy}
      onClick={onClick}
    >
      {children}
    </button>
  );
};

export const Navigation = () => {
  const user = useSelector((state: any) => state?.login?.user);
  const history = useHistory();
  const { data: prefs } = usePreferences();
  const isDDAIMetricsEnabled = useFeatureFlag(
    PracticeFeatureFlagKey.DDAI_METRICS_ENABLED,
  )?.value;
  const [directorPracticeId, setDirectorPracticeId] = useAtom(
    directorPracticeIdAtom,
  );
  const [isViewingEngagement, setIsViewingEngagement] = useAtom(
    isViewingEngagementAtom,
  );

  const shouldShowDDAIMetrics =
    isDDAIMetricsEnabled && (!isDirectorUser(user) || !!directorPracticeId);

  const hideHuddle = useShouldHideHuddle();

  const { isDirectorPracticeViewUser } = useDirectorPracticeView();
  const shouldShowHuddle =
    user?.primaryPracticeId || isDirectorPracticeViewUser;

  const isClaimsOnly = prefs?.standaloneClaimsEnabled;

  const isInsightsEnabled =
    isDirectorUser(user) &&
    prefs?.enableInsights &&
    prefs?.enableInsights.length > 0;

  const hasInsightsDashboards =
    prefs?.enableInsights &&
    prefs.enableInsights.filter((dashboard) => dashboard !== 'ENGAGEMENT')
      .length > 0;

  const hasEngagementDashboard =
    prefs?.enableInsights && prefs.enableInsights.includes('ENGAGEMENT');

  const shouldShowAutoVerifyTab = prefs?.isInsuranceEligibilityEnabled ?? false;
  const shouldShowClaimsTab = prefs?.cleanClaims ?? false;
  const shouldShowNotesTab =
    (prefs?.isVideaNotesEnabled || prefs?.enableVoiceNotesStandalone) ?? false;
  const shouldShowReferralsTab = prefs?.enableVideaReferral ?? false;

  // Check if voice notes standalone is enabled
  const isVoiceNotesStandalone = prefs?.enableVoiceNotesStandalone ?? false;

  // If only 1 tab available (Huddle or Practices, or Claims), don't show the tab
  if (
    isClaimsOnly ||
    (!isInsightsEnabled &&
      ((!shouldShowClaimsTab &&
        !shouldShowDDAIMetrics &&
        !shouldShowNotesTab &&
        !shouldShowAutoVerifyTab &&
        !shouldShowReferralsTab) ||
        !shouldShowHuddle))
  ) {
    return null;
  }

  return (
    <div className="vai-flex vai-items-center vai-gap-8">
      {/* for the Huddle practices directors only (with Insights) */}
      {isDirectorUser(user) && isInsightsEnabled && (
        <>
          {hasInsightsDashboards && (
            <div className="vai-text-white vai-space-x-2">
              <NavigationItem
                onClick={() => {
                  setDirectorPracticeId(null);
                  history.push(
                    prefs?.enableInsightsPOC
                      ? `${routesConstants.INSIGHTS}`
                      : `${routesConstants.SWITCHBOARD}`,
                  );
                  setIsViewingEngagement(false);
                }}
                isSelected={
                  (history.location.pathname.includes(
                    routesConstants.SWITCHBOARD,
                  ) &&
                    !isViewingEngagement) ||
                  history.location.pathname.includes(routesConstants.INSIGHTS)
                }
              >
                <Trans>Insights</Trans>
              </NavigationItem>
            </div>
          )}
          {hasEngagementDashboard && (
            <div className="vai-text-white vai-space-x-2">
              <NavigationItem
                onClick={() => {
                  setDirectorPracticeId(null);
                  history.push(`${routesConstants.SWITCHBOARD}`);
                  setIsViewingEngagement(true);
                }}
                isSelected={
                  history.location.pathname.includes(
                    routesConstants.SWITCHBOARD,
                  ) && isViewingEngagement
                }
              >
                <Trans>Engagement</Trans>
              </NavigationItem>
            </div>
          )}
          <div className="vai-inline-block vai-h-[34px] vai-w-[1px] vai-bg-[#FFFFFF33]"></div>
        </>
      )}
      {/* For the practice level user */}
      {(directorPracticeId || !isDirectorUser(user)) && (
        <>
          {!isClaimsOnly && !isVoiceNotesStandalone && (
            <NavigationItem
              isSelected={history.location.pathname.includes(
                routesConstants.UPLOADER,
              )}
              onClick={() => history.push(routesConstants.UPLOADER)}
            >
              {hideHuddle ? (
                <Trans>Clinical Assist</Trans>
              ) : (
                <Trans>Daily Dashboard</Trans>
              )}
            </NavigationItem>
          )}
          {shouldShowAutoVerifyTab && !isVoiceNotesStandalone && (
            <NavigationItem
              dataCy="autoverify-tab"
              isSelected={history.location.pathname.includes(
                routesConstants.AUTOVERIFY,
              )}
              onClick={() => history.push(routesConstants.AUTOVERIFY)}
            >
              <Trans>AutoVerify</Trans>
            </NavigationItem>
          )}
          {shouldShowClaimsTab && !isVoiceNotesStandalone && (
            <NavigationItem
              dataCy="claims-tab"
              isSelected={history.location.pathname.includes(
                routesConstants.CLAIMS,
              )}
              onClick={() => history.push(routesConstants.CLAIMS)}
            >
              <Trans>Clean Claims</Trans>
            </NavigationItem>
          )}
          {shouldShowNotesTab && (
            <NavigationItem
              dataCy="ambient-tab"
              isSelected={history.location.pathname.includes(
                routesConstants.AMBIENT,
              )}
              onClick={() => history.push(routesConstants.AMBIENT)}
            >
              <Trans>Voice Notes</Trans>
            </NavigationItem>
          )}
          {prefs?.enableVideaReferral && !isVoiceNotesStandalone && (
            <NavigationItem
              dataCy="referrals-tab"
              isSelected={history.location.pathname.includes(
                routesConstants.REFERRALS,
              )}
              onClick={() => history.push(routesConstants.REFERRALS)}
            >
              <Trans>Referrals</Trans>
            </NavigationItem>
          )}
        </>
      )}
      {/* for the Director user */}
      {isDirectorUser(user) && !directorPracticeId && (
        <>
          {/* Show claims tab for director users when standaloneClaimsEnabled is true */}
          {isClaimsOnly ? (
            <PracticesDialog title="claims">
              <DialogTrigger asChild className="vai-text-white">
                <NavigationItem
                  isSelected={history.location.pathname.includes(
                    routesConstants.CLAIMS,
                  )}
                >
                  <Trans>Clean Claims</Trans>
                </NavigationItem>
              </DialogTrigger>
            </PracticesDialog>
          ) : (
            <PracticesDialog title="huddle">
              <DialogTrigger asChild className="vai-text-white">
                <NavigationItem
                  isSelected={history.location.pathname.includes(
                    routesConstants.UPLOADER,
                  )}
                >
                  <Trans>Daily Dashboard</Trans>
                </NavigationItem>
              </DialogTrigger>
            </PracticesDialog>
          )}
        </>
      )}

      {shouldShowDDAIMetrics && (
        <NavigationItem
          isSelected={history.location.pathname.includes(
            routesConstants.ANALYTICS,
          )}
          onClick={() => history.push(routesConstants.ANALYTICS)}
        >
          <Trans>AI Impact</Trans>
        </NavigationItem>
      )}
    </div>
  );
};
